import { globals } from 'config/globals';
import { Controller } from 'core/Controller';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { PixiController } from 'app/services/PixiController';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { DepartmentType, SectionType, ProjectSlugType, ViewType, DirectionalKeyType } from 'app/data/Enums';
import { HitArea } from 'app/module/global/HitArea';


import { ViewReadyEvent } from 'app/events/ViewReadyEvent';
import { DebugMouseWheelEvent } from 'app/events/DebugMouseWheelEvent';
import { DebugKeypressEvent } from 'app/events/DebugKeypressEvent';

import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { WorkConfig } from 'app/model/WorkConfig';
import { UIController } from 'app/views/ui/UIController';
import { NavMenuController } from 'app/chrome/navmenu/NavMenuController';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import Config from 'config/settings';
import { constrain } from 'utils/math';
import DelayHelper from 'utils/timers';
import { ProjectsMenuController } from 'app/views/projectsmenu/ProjectsMenuController';
import SiteTransitions from 'app/services/SiteTransitions';
import ComboCounter from 'utils/ComboCounter';
import { Simple } from 'utils/analytics';

import { WorkView } from './WorkView';
import { WorkScene } from './WorkScene';
import { WorkDrag } from './WorkDrag';
import { WorkPaging } from './WorkPaging';
import { WorkLoading } from './WorkLoading';
import { WorkExploreProjectData } from './work.types';
import { device } from 'utils/device';

/**
 * Work section controller (instantiated three times: Screens / Spaces /
 * Systems — see src/index.ts). Orchestrates the per-concern sub-objects in
 * this folder (see work.types.ts for the decomposition pattern). Keeps: the
 * lifecycle hooks, the route entries, the full public surface used by
 * AppController / SiteTransitions / ProjectController / PreloadController,
 * every Events.on(...) registration, and all cross-controller access
 * (this.get(...) / injected controllers).
 */
export class WorkController extends Controller {

	private _active: boolean = false;
	private _isSetup: boolean = false; //Is setup up ready to view
	private _homeViewReady: boolean = false; //Flagged when home loads

	private _department: DepartmentType;

	private _json: WorkConfig;
	private _innerWidth: number;
	private _disableToProject: boolean = false;


	private _mouseController: MouseController;
	private _pixiController: PixiController;
	private _projectsMenuController: ProjectsMenuController;
	private _uiController: UIController;
	private _navMenuController: NavMenuController;

	private _timeouts: any = {};
	private _tweens: any = {};
	private _intervals: any = {};

	private _isDeepLink: boolean = false;

	private _allowResize: boolean = true;

	private _comboCounter: ComboCounter = new ComboCounter();

	private _startDragPromptProgress = 0;
	private _trackHidePrompt: boolean = false;
	private _trackIntroGuide: boolean = false;

	//Per-concern sub-objects (see work.types.ts for the pattern):
	private _loading: WorkLoading;
	private _view: WorkView;
	private _scene: WorkScene;
	private _drag: WorkDrag;
	private _paging: WorkPaging;

	//Called when work is routed directly
	//(Never actually calls work as there is only /spaces/ and /screens/ routes)
	constructor(id: string) {
		//Listen for view ready event
		super(id);

		this._loading = new WorkLoading();

		this._view = new WorkView(this.DOM, {
			getDepartment: () => this._department,
			onContainerReady: (container: Element) => {
				this.container = container as HTMLElement;
				this.module.createModulesInTarget(this.container as HTMLElement);
			},
			onLoaderComplete: () => this.animateToFirstProject(),
			onIntroProgress: (introX: number, introProgress: number) =>
				this.get('home').updateWorkTransitionParticles(introX, introProgress)
		});

		this._scene = new WorkScene({
			getDepartment: () => this._department,
			isActive: () => this._active,
			getMouseCords: () => this._mouseController.getMouseCords(),
			getHitArea: (index: number) => this.module.get(`${this._department}-hitarea-${index}`) as HitArea,
			onExploreProject: (data: WorkExploreProjectData) => this._onTileExploreProject(data)
		});

		this._drag = new WorkDrag({
			isActive: () => this._active,
			onFrame: () => this._updateTiles(),
			onStartDrag: () => {
				this._mouseController.setDragDown(true, null, 0.7);

				//Process the tile hit area logic (i.e if not moving too fast, and shoing then check for a click)
				this._scene.processHitAreaMouseDown();

				//User is draging so no longer need to worry about next/back/paging animation
				this._paging.disableAutoUpdate = false;
			},
			onStopDrag: () => {
				this._mouseController.setDragDown(false);
				//Process the tile hit area logic (i.e if not moving too fast, and shoing then check for a click)

				if (this._active) {
					Simple.event('work_stop_drag', { department: this._department });
				}
			},
			onSnap: (snapIndex: number) => {
				this._mouseController.setDragDown(false);
			},
			applyVelocityRotation: (radians: number) =>
				this._scene.tiles.forEach((tile) => tile.setVelocityRotation(radians))
		});

		this._paging = new WorkPaging(this.DOM, {
			getDepartment: () => this._department,
			getProject: (index: number) => this._json.projects[index],
			onGotoProject: (index: number) => this._gotoProject(index),
			queueIntroPrompt: () => {
				this._comboCounter.add(() => {
					if (this._drag.progress < 0.02) {
						this._uiController.showDragPrompt(0);
						this._trackIntroGuide = true;
					}
				}, 5000);
			}
		});

		Events.on(ViewReadyEvent, this._onViewReady.bind(this));

		//Hide text for
	}

	//NOTE: an abandoned Comlink web-worker experiment used to live here
	//(createComWorker / processCommLink + _comWorker/_comlink fields): it
	//wrapped a Worker('/js/ParticleWorker.js') with Comlink and pushed tile
	//particle updates through it. Nothing ever called either method, no Comlink
	//library is loaded anywhere, and /js/ParticleWorker.js does not exist — it
	//would have thrown on first use. The worker-side half survives as
	//app/module/work/WorkTileParticleWorker.ts if the idea is ever revived.

	public init(): void {
		this._active = true;
		this._isDeepLink = true;
		let href = (window as any).mode == 'lite' ? '/' : `/${this._department}`;

		this._projectsMenuController.setSelectedDepartment(this._department, false, true);

		let title = this._department === DepartmentType.SPACES ? 'Spaces' : 'Screens';

		if ((window as any).mode == 'lite') {
			title = 'Interactive Screens & Spaces';
		}

		//Set the opening state to the homepage:
		let openingState: HistoryStateModel = {
			section: SectionType.WORK,
			subsection: this._department,
			title: title,
			href: href
		};
		Events.dispatch(AppEvents.SET_INTIAL_STATE, openingState);

		Simple.event('deeplink', { section: SectionType.WORK, department: this._department });

		if (this._scene.workContainer) {
			this._scene.workContainer.alpha = 1;
			this._scene.workContainer.y = 0;
		}
	}

	//Spaces route:
	public spaces(): void {
		this._department = DepartmentType.SPACES;
		this.init();
	}

	//Screens route:
	public screens(): void {
		this._department = DepartmentType.SCREENS;
		this.init();
	}

	public systems(): void {
		this._department = DepartmentType.SYSTEMS;
		this.init();
	}

	/** Unless routed then class doesn't know if spaces / screens, so set directly */
	public setDepartment(department: DepartmentType): void {
		this._department = department;
	}

	//Getters
	//-----------------------------------------------------------------------

	public isFullyLoaded(): boolean {
		return this._isSetup;
	}

	/** Get if ready to view. This is called when a deep link or loading is happening */
	public isReadyToView(state: HistoryStateModel): boolean {
		//This is called when checking if the view is ready to load
		this._loading.testReadyToSetup();
		return this._loading.readyToSetup;
	}

	/** Is actually ready to 'show' - allow for delay in WebGL content being ready */
	public readyForDeeplink(state: HistoryStateModel): boolean {
		//If this function is being called then process is checking if it can show
		//So if its ready to setup but not actually been created then can setup here
		if (this._loading.readyToSetup && !this._isSetup) {
			this._setup();
		}

		return this._loading.readyToSetup && this._isSetup;
	}

	public subsectionStateChange(state: HistoryStateModel): void {
		if (!state.workIndex) {
			state.workIndex = 0;
		}

		this._gotoProject(state.workIndex, false);
	}

	public showMouseCursor() {
		this._mouseController.setDragCursorActive(true);
		this._mouseController.setHiddenState(false);
	}

	//Setters ----------------------------------------------------------------
	/** AJAX loaded HTML  for the section: */
	public setHTML(html: string): void {
		if (!html) {
			return;
		}

		this._view.storeHTML(html);
		this._loading.setFlag('dom');
		this._loading.testReadyToSetup();

		if (this._loading.readyToSetup) {
			this._setup();
			return;
		}

		if (this._view.injectionQueued) {
			this._view.injectHTML();
		}
	}

	/** Set JSON for project items and settings */
	public setJSON(json: WorkConfig): void {
		this._json = json;

		this._json.projects = this._json.projects.filter((project, index, self) => {
			return project.active !== false && self.indexOf(project) === index;
		});

		this._json.projects = this._json.projects.sort((a, b) => (a.order > b.order ? 1 : -1));

		this._loading.setFlag('json');
		this._loading.testReadyToSetup();

		if (this._loading.readyToSetup) {
			this._setup();
			return;
		}
	}

	/** History state has been changed by / back forward */
	public setState(state: HistoryStateModel): void {
	}

	//Set controller depencies (set from app controller):
	public setControllers(
		mouse: MouseController,
		pixi: PixiController,
		projectsMenu: ProjectsMenuController,
		ui: UIController,
		nav: NavMenuController
	): void {
		this._mouseController = mouse;
		this._pixiController = pixi;
		this._projectsMenuController = projectsMenu;
		this._uiController = ui;
		this._navMenuController = nav;

		this._scene.setPixiController(pixi);
	}

	//Pixi dependecies (set in app controller):
	public setPixi($pixi: PIXI.Application, $backgroundContainer: PIXI.Container, $workContainer: PIXI.Container): void {
		this._scene.setPixi($pixi, $backgroundContainer, $workContainer, this._isDeepLink, this._department);
	}

	//Public -------------------------------------------------------------------
	//DOMReady. Add DOM element refs and event listeners:
	public DOMReady(): void {
		this._innerWidth = window.innerWidth;

		if (this._active) {
			//Flag the is active
			this._loading.setFlag('dom');
			this._loading.testReadyToSetup();
		}

		//If active then directly loaded so
		if (this._loading.readyToSetup) {
			this._setup();
			return;
		}

		//If injection of HTML is queded then preform that that complete the setup
		this._testInjectonQeued();
	}

	/** Que for showing from home screen. Basically inject the HTML, which then triggers the _setup process */
	public que(): void {
		if (this._view.hasHTML) {
			this._view.injectHTML();
		} else {
			this._view.queueInjection();
		}
	}

	/** Freeze */
	public freeze(): void {
		this._active = false;
		this._drag.setActive(false);
		this._mouseController.setDragCursorActive(false);
		this._timeouts.freeze = DelayHelper.setTimeout(() => this._cleanupAfterFreeze(), 500);
	}

	/** If a deeplink is fired while project is opened then may need to reset this view  */
	public freezeFromProjectView(): void {
		if (this._scene.workContainer) {
			this._scene.resetAllProjects();
			this._scene.workContainer.visible = false;
			this.DOM.removeClass(this.container, '-active');
		}
	}

	/** Reset is always called before showing to put in correct state. Seperate from 'show' as a deplay might be needed between the 2 */
	public reset(oldState: HistoryStateModel = null, isDeeplink: boolean = false): void {
		let fromMenu = oldState && oldState.section == SectionType.ABOUT;

		if (fromMenu) {
			return;
		}

		//0.) Generic across all types of show:
		this._resetIntervalsAndAnimations();

		this._view.resetIntroX();
		this._paging.guideStartProgress = 0;
		this._mouseController.setColour('light');

		if (this._isSetup && (oldState === null || oldState.section !== SectionType.PROJECT)) {
			this._resetProgress();
			this._scene.resetTiles();
			this._paging.reset();
			this._paging.setActiveIndex(0);
		}

		//Show the container by making it active
		this.DOM.addClass(this.container, '-active');

		//Is a deeplink for get ready to show coming direct from Preoader
		if (isDeeplink) {
			this._resetForDeeplink();
			return;
		}

		//If transition from gome then the content needs to animate updwards
		if (oldState.section === SectionType.HOME) {
			this._resetForHome();
		}

		//If transition back from project
		if (oldState.section === SectionType.PROJECT) {
			this._resetForProject();
		}
	}

	private _pendingOldHistoryState: HistoryStateModel = null;

	public showFromTransitionLoader() {
		this.show(this._pendingOldHistoryState);
	}

	/**
	 * Show after preload, navigation or seamless transition from other section
	 * @param {HistoryStateModel} oldState - The previous view state
	 * @param {boolean} isDeepLink - Is coming from the preloader state rather than a seamless transition
	 */
	public show(oldState: HistoryStateModel, isDeeplink: boolean = false): void {
		this._loading.testReadyToSetup();

		if (!this._loading.readyToSetup) {
			this._pendingOldHistoryState = oldState;
			return;
		}

		this._pixiController.unpause();
		this.get('ui').unpause();

		this._scene.workContainer.renderable = true;
		this._scene.workContainer.visible = true;

		this._projectsMenuController.setSelectedDepartment(this._department, true, this._isDeepLink);

		this._pixiController.setAllowSlowFrameCheck(true);

		//If no state then coming from the intial preload / deeplink
		if (!oldState || isDeeplink) {
			this._showFromDeeplink();
			return;
		}

		//If coming from home then section needs to aimate updwards
		if (oldState.section === SectionType.HOME) {
			this._showFromHome();
		}

		if (oldState.section === SectionType.WORK) {
			this._showFromHome();
		}

		//If coming from project then
		if (oldState.section === SectionType.PROJECT) {
			this._showFromProject();
		}

		if (oldState.section === SectionType.ABOUT) {
			setTimeout(() => {
				this.showDragPromptFromProject();
			}, 300);
		}
	}

	/**
	 * after section is moved away from. The new state is passed so can work out how to transition out
	 * @param {HistoryStateModel} newState
	 */
	public hide(newState: HistoryStateModel = null): void {
		if (newState.section === SectionType.ABOUT) {
		} else if (newState.section === SectionType.HOME) {
			this._hideForHome();
		} else if (newState.section === SectionType.WORK) {
			//Cross link......
			this._hideForProjectFilter(newState);
		}

		//Hide the work only CSS bits:
		if (newState.section !== SectionType.WORK && newState.section !== SectionType.ABOUT) {
			DelayHelper.setTimeout(() => {
				this.DOM.removeClass(document.querySelector('body'), '-workactive');
			}, 800);
		}

		this._active = false;
	}

	/** Pause (For when nav menu is showing) */
	public pause(fromProject: string | boolean = false, skipTransitions = false): void {
		if (fromProject === true) {
			this._active = false;
			this._scene.activeTile.pause();
		} else {
			this._setInteractionEnabled(false);
			this._pixiController.setAllowSlowFrameCheck(false);
		}
	}

	public pauseForMenu(direction = 'side'): void {
		this.pause(false, false);

		if (direction == 'side') {
			this.DOM.addClass(SiteTransitions.$body, 'app-paused');
			this.DOM.addClass(SiteTransitions.$body, '-pausedside');
		} else {
			this.DOM.addClass(SiteTransitions.$body, 'app-paused');
		}
	}

	/** Unpause (For when nav menu is hidden) */
	public unpause(fromProject = false, skipTransitions = false): void {
		this._mouseController.resetToHorizontal();
		this.get('ui').unpause();

		if (fromProject === true) {
			this._active = true;
			this._scene.activeTile.unpause();
		} else {
			this._setInteractionEnabled(true);
			this._disableToProjectClicks();
			this._pixiController.setAllowSlowFrameCheck(true);
		}

		this.DOM.removeClass(SiteTransitions.$body, 'app-paused');
		this.DOM.removeClass(SiteTransitions.$body, '-pausedside');

		this.resize();
	}

	public pauseOnProject() {
		this._active = false;
		this._scene.activeTile.unpause();
	}

	public unPauseOnProject() {
		this._active = true;
		this._scene.activeTile.unpause();
	}

	/**
	 * Animate x to progress to a point in time
	 * @param {number} index -  will most probbaly '0' be for intro slide:
	 */
	public animateProgressToValue(index: number): void {
		this.unpause();
		this._scene.activeProjectIndex = index;
		this._drag.setIndex(index);
	}

	public resizeOnProject() {
		this._scene.requestHardSizeReset();
	}

	/** Resize event. Redo all the tiles */
	public resize(fromOrientationChange: boolean = false): void {
		if (!this._allowResize && !fromOrientationChange) {
			return;
		}

		if (this._scene.activeTile) {
			this._scene.activeTile.resize();
		}

		if (!this._active) {
			return;
		}

		this._setVirtualWidth();
		this._innerWidth = window.innerWidth;

		this._scene.resizeTiles();
	}

	/** RequestAnimationFrame interval tick */
	public tick(delta: number = 1): void {
		if (this._isSetup && this._active) {
			this._scene.processHitAreaUpdate();
			this._paging.checkGuidePassed(this._drag.progress);
			this._view.checkBackToHomeVisible(this._homeViewReady, this._drag.progress, this._drag.velocity, this._scene.tileRatioShare);
			this._checkPromptHide();
			this._paging.setAutoPaging(this._drag.progress);

			if (this._view.loaderActive) {
				this._view.tickFakeLoader();
			}

			this._comboCounter.tick();
		}
	}

	/**
	 * Partciles spritesheet loaded so flag and deal with
	 * @param {PIXI.Spritesheet} spritesheet -  spritesheet of particle eleemnts
	 */
	public pixiParticlesLoaded(spritesheet: PIXI.Spritesheet): void {
		this._scene.setParticlesSpritesheet(spritesheet);
		this._checkPixiResourcesAreReady();

		return;
	}

	/**
	 * Partciles spritesheet loaded so flag and deal with
	 * @param {PIXI.Spritesheet} spritesheet -  spritesheet of particle eleemnts
	 */
	public pixiParticles32Loaded(spritesheet: PIXI.Spritesheet): void {
		this._scene.setParticlesSpritesheet32(spritesheet);
		this._checkPixiResourcesAreReady();
	}

	//Background spritesheet loaded so flag and deal with
	public pixiBackgroundsLoaded(spritesheet: PIXI.Spritesheet, department: DepartmentType): void {
		this._scene.setBackgroundSpritesheet(spritesheet);
		this._checkPixiResourcesAreReady();
	}

	//Go from work > project on history state:
	public historyStateToProject(stateObject: HistoryStateModel): void {
		//TODO add proper title text:
		if (this._paging.focusedIndex == stateObject.projectIndex) {
			this._projectSelected(stateObject.projectSlug, stateObject.projectIndex, stateObject.href, 'TITLE', true);
		} else {
			this._gotoProject(stateObject.projectIndex, false);
			SiteTransitions.addCounter(() => {
				console.log('Project selected counter done');
				this._projectSelected(stateObject.projectSlug, stateObject.projectIndex, stateObject.href, 'TITLE', true);
				SiteTransitions.fixCloseShow();
			}, 700);
		}
	}

	public startExitScroll(maskStyle): void {
		this._scene.activeTile.startExitScroll(maskStyle);
	}

	public exitScroll(planeY): void {
		if (this._scene.activeTile) {
			this._scene.activeTile.exitScroll(planeY);
		}
	}

	public hideForSwitch() {
		this._scene.workContainer.alpha = 0;
	}

	public showDragPromptFromProject() {
		let realProgress = this._paging.focusedIndex / (this._scene.tiles.length - 1);
		this._startDragPromptProgress = this._drag.progress;
		this._uiController.showDragPrompt(realProgress);
		this._trackHidePrompt = true;
	}

	//Private methods:
	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------

	//-------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------

	private _disableToProjectClicks() {
		this._disableToProject = true;
		DelayHelper.setTimeout(() => (this._disableToProject = false), 1000);
	}

	/** Test if injection is qued. If so then inject the html in. */
	private _testInjectonQeued(): void {
		if (this._view.injectionQueued && this._loading.readyToSetup) {
			this._view.injectHTML();
		}
	}

	/** DOM/PIXI ready so setup the view to interactive with */
	private _setup(): void {
		if (this._isSetup) {
			return;
		}

		//If container doesn't exists then create.
		if (!this.container) {
			this._view.injectHTML(() => this._setup());
			return;
		}

		this._view.queryRefs(this.container);

		//Set the swipe prompt feature:

		this._addListeners();
		this._setupPixi();
		this._paging.setup(this.container, this._department);

		this._isSetup = true;

		//Trigger the VIEW_SETUP event, which can be picked up by any loading processes:
		let view: ViewType = this._department === DepartmentType.SCREENS ? ViewType.SCREENS : ViewType.SPACES;

		if (window.mode == 'lite' && ViewType.SPACES) {
			view = ViewType.ALL;
		}

		Events.dispatch(
			new ViewReadyEvent({
				view: view
			}),
			{ delay: 1 }
		);
	}

	/** Create the animated moth */

	/** Set the virtual width for the break points */
	private _setVirtualWidth(): void {
		if (this._scene.tiles) {
			this._drag.setVirtualWidthForTiles(this._scene.tiles.length);
		}
	}

	/** Add Listeners for */
	private _addListeners(): void {
		//Listen for tile explore clicks

		Events.on('click', `.worktitle__link`, this._onExploreProjectClick.bind(this));
		Events.on('click', `.show__home__button`, this._onBackToHomeClick.bind(this));
		Events.on(DebugMouseWheelEvent, this._onMouseWheel.bind(this));
		Events.on(DebugKeypressEvent, this._onKeyPress.bind(this));

		Events.on('click', `body`, this._processHitAreaClick.bind(this));

		Events.on('mouseover', '.show__home__button', (e: MouseEvent) => {
			if (!this._active) {
				return;
			}

			console.log('Back mouse over');
			this.DOM.addClass(SiteTransitions.$body, 'work-back-hover');
		});

		Events.on('mouseout', '.show__home__button', (e: MouseEvent) => {
			if (!this._active) {
				return;
			}
			console.log('Back mouse out');
			this.DOM.removeClass(SiteTransitions.$body, 'work-back-hover');
		});
	}

	/** Check the state of availble ressources so can load the */
	private _checkPixiResourcesAreReady(): void {
		if (this._scene.hasRequiredSpritesheets()) {
			this._loading.setFlag('pixi');
		}

		this._loading.testReadyToSetup();

		//If active and ready then setup
		if (this._loading.readyToSetup) {
			this._setup();
			return;
		}

		//If injection of HTML is queded then preform that that complete the setup
		this._testInjectonQeued();
	}

	/** Setup PIXI elements */
	private _setupPixi(): void {
		this._scene.createFirstSlideBG();
		this._scene.createTiles(this._json.projects, this.container as HTMLElement);

		//1st tile is on at 50% of it's timeline
		//That means is there are x5 then it starts at 0.1 ratio
		//Set activeProjectIndex to debug or jump straight to project)
		this._drag.seedProgress(this._scene.activeProjectIndex * this._scene.tileRatioShare);

		//Update the progress to move to correct start point
		this._scene.updateTileProgress(this._drag.progress, this._drag.velocity);

		this._drag.createBreakpoints(this._scene.tiles.length, this._json, this.get('Mouse') as MouseController);
	}

	/** Explore from holding down on project tile (wired into each tile by WorkScene) */
	private _onTileExploreProject(data: WorkExploreProjectData): void {
		if (this._paging.focusedIndex == data.index + 1) {
			SiteTransitions.startNormalTransition();
			this._projectSelected(data.slug, data.index + 1, data.href, data.title);

			Simple.event('explore_project_press', {
				department: this._department,
				slug: data.slug
			});
		}
	}

	/** Per-frame update fan-out (called from WorkDrag on each progress tick) */
	private _updateTiles(forceUpdate = false) {
		this._scene.updateTileProgress(this._drag.progress, this._drag.velocity, forceUpdate);
		this._view.setIntroVisibleState(this._drag.progress, this._scene.introActiveRange);
		this._view.updateIntroProgress(this._drag.progress, this._scene.introActiveRange, this._innerWidth);
		this._scene.updateBGAnimation(this._drag.progress, this._innerWidth);
		this._drag.processSnap(this._paging.focusedIndex);
		this._processHidePrompt();
	}

	private _processHidePrompt() {
		if (!this._mouseController.guideFollowActive && !this._trackIntroGuide) {
			return;
		}

		if (this._drag.progress > 0.05) {
			this._mouseController.guideFollowActive = false;
			this._trackIntroGuide = false;
			this._uiController.hideDragPrompt();
		}
	}

	private _processHitAreaClick(e: MouseEvent): void {
		if (!this._active || this._disableToProject || globals.slowFpsShowing) {
			return;
		}

		let cords = { x: e.clientX, y: e.clientY };
		this._scene.processHitAreaClick(cords);
	}

	//Reset methods
	//-----------------------------------------------------------------------------------

	/** Reset anything that is happening */
	private _resetIntervalsAndAnimations(): void {
		Object.keys(this._tweens).forEach((key: string) => {
			this._tweens[key].kill();
		});
		Object.keys(this._timeouts).forEach((key: string) => {
			clearTimeout(this._timeouts[key]);
		});
		Object.keys(this._intervals).forEach((key: string) => {
			clearInterval(this._intervals[key]);
		});
	}

	/** Reset for deeplink (opening, nav jump or history state) */
	private _resetForDeeplink(): void {
		//Need to also reset the background so that it is
		this.DOM.addClass(SiteTransitions.$body, 'transition-home-to-work');

		//2.) Make sure the intro is showing:
	}

	/** Reset for transition from home where the elements side in  */
	private _resetForHome(): void {
		this._scene.workContainer.visible = false;
	}

	/** Reset when coming from project. Nothing special needed as it should be in required state */
	private _resetForProject(): void {
		//NOTHING NEEDS TO BE RESET HERE. SHOULD BE IN THE REQUIERD STATE ALREADY (Maybe resize?)
	}

	/** Reset the timeline right to start '0' progress */
	private _resetProgress(): void {
		this._paging.focusedIndex = 0;
		this._drag.resetProgress();
	}

	/** Cleanup after freezing */
	private _cleanupAfterFreeze(): void {
		this._scene.workContainer.visible = false;
		this.DOM.removeClass(this.container, '-active');
		this._resetProgress();
		this._scene.resetTiles();
	}

	//Show methods
	//-----------------------------------------------------------------------------

	private _showAndSetupPaging(deeplink: boolean = false, fromProject: boolean = false) {
		if (this._paging.isCreated) {
			this._view.showDragToExploreGuide(deeplink, fromProject);
			this._paging.active = true;
		}
	}

	/** Show from the preloader state */
	private _showFromDeeplink(): void {
		this._active = true;

		//Stop scrolling before ui button comes in:
		this._drag.setActive(true);
		this._mouseController.setDragCursorActive(true);
		this._showAndSetupPaging(true, false);

		//Set the opening state of the slider:
		this.resize();
		this._scene.updateTileProgress(this._drag.progress, this._drag.velocity);

		this.DOM.addClass(SiteTransitions.$body, '-prompt-no-follow-cursor');

		setTimeout(() => {
			this.DOM.removeClass(SiteTransitions.$body, 'transition-home-to-work');
		}, 600);

		setTimeout(() => {
			this._uiController.showUiItem('projects');
			this._uiController.showUiItem('backToHome');
			this._uiController.showUi();
		}, 1000);

		this._comboCounter.add(this.showPagination.bind(this), 1500);
		this._comboCounter.add(this.showDragPrompt.bind(this), 2000);
	}

	/**
	 * Called from SiteTransitions.ts flows when transitiong from Home
	 * Also called as part of deep link loading:
	 */
	public showPagination() {
		this._paging.showGuide();
	}

	/**
	 * Call from SiteTransitions.ts flows
	 */
	public showDragPrompt() {
		console.log('WorkController.showDragPrompt()');

		if (this._drag.progress < 0.01) {
			this._uiController.showDragPrompt();
			this._mouseController.guideFollowActive = true;
		}
	}

	/** Show from transition from Home
	 * Note: Transitions flow in SiteTransitions.ts
	 */
	private _showFromHome(slideToProjectOne: boolean = true): void {
		this._mouseController.setDragCursorActive(false);
		this._mouseController.setHiddenState(true);

		if (!this._isSetup) {
			this._setup();
		}

		this._active = true;
		this._scene.updateBGAnimation(this._drag.progress, this._innerWidth);
		this.resize();

		this._paging.showGuide();
		this._view.backToHomeVisible = false;

		if (this._paging.isCreated) {
			this._paging.hide();
		}
	}

	public hideWorkPixiContainer() {
		this._scene.workContainer.visible = false;
		this._scene.workContainer.alpha = 0;
	}

	public showWorkPixiContainer() {
		this._scene.workContainer.visible = true;
		this._scene.workContainer.alpha = 1;
	}

	public animateToFirstProject() {
		this._drag.setActive(true);
		this._mouseController.setDragCursorActive(true);
		this._mouseController.setHiddenState(false);
		this._drag.slideOnFirstProject();
		this.DOM.addClass(this._view.$loader, '-loaded');
		this.DOM.removeClass(document.querySelector('body'), 'in-transition');
	}

	private _showFromProject(): void {}

	/** Show from transition from Active / Zoomed in project  */
	private _switchFromProject(): void {
		globals.activeWorkController = null;

		//1.) Reverse the animation of the tile to zoom back into the timeline
		if (this._scene.activeTile) {
			this._scene.activeTile.backFromProject();
		}

		this._active = true;
		this._updateTiles(true);

		this._view.showBackToHome();

		this._navMenuController.delayInfoButtonInteraction();
		this._projectsMenuController.disableHoverForTransition();
		this._mouseController.stopVerticalScroll();
		this._mouseController.setColour('light');

		this._showAndSetupPaging(false, true);

		//MAKE CSS based:
		(this.get('ui') as UIController).showBackFromProject();

		//2.) Show surrounding tiles again:

		SiteTransitions.clearWorkTileTweens();

		//3.) Reset flags/vars and activate this view controller again (clears the active tile)
		this._scene.showTilesFromProject();

		SiteTransitions.onWorkZoomBackComplete = () => {
			this._paging.guideStartProgress = this._drag.progress;
			this._active = true;
			this._drag.setActive(true);
			this._mouseController.setDragCursorActive(true);
			this._mouseController.setDarkTheme(false);
			this._mouseController.setHiddenState(false);
			this._paging.show();
			this._disableToProject = false;
		};
	}

	//Hide methods
	//-------------------------------------------------------------------------------


	public deactivateWorkSection() {
		this.DOM.removeClass(this.container, '-active');
		this._scene.workContainer.visible = false;
	}

	//** Hide to move to info section. Means hiding pixi and Home HTML elements */

	private _hideForProjectFilter(newState: HistoryStateModel) {
		SiteTransitions.startWorkToWork(newState.subsection as DepartmentType, this._department);

		this._projectsMenuController.setSelectedDepartment(null, true);
	}

	private _hideForHome(): void {
		SiteTransitions.startWorkToHome();
		this._projectsMenuController.setSelectedDepartment(null, true);

		//Remove back to home:
		this._view.canShowBack = false;
		this.DOM.removeClass(this._view.$backButton, '-show');
	}

	//-------------------------------------------------------------------------------

	/** Set interactoon to on/off */
	private _setInteractionEnabled(enabled: boolean): void {
		this._active = enabled;
		this._drag.setActive(enabled);
		this._mouseController.setDragCursorActive(enabled);
	}

	private _gotoProject(index: number, historyState: boolean = true): void {
		this._drag.setIndex(index);
		this._setProjectIndex(index, historyState);
		this._paging.disableAutoUpdate = true;


		clearInterval(this._timeouts.disablePaging);
		this._timeouts.disablePaging = DelayHelper.setTimeout(() => {
			this._paging.disableAutoUpdate = false;
		}, 1000);
	}

	private _setProjectIndex(index: number, addHistoryState: boolean = true): void {
		this._scene.activeTile = this._scene.tiles[index];
		this._paging.setActiveIndex(index);
		this._paging.focusIndexChanged(index);
	}

	//Explore project has been click
	//---------------------------------------------------------------

	private _onExploreProjectClick(e: MouseEvent): boolean {
		if (globals.slowFpsShowing) {
			return;
		}

		if (this._disableToProject) {
			return;
		}

		e.stopPropagation();

		e.stopImmediatePropagation();
		e.preventDefault();
		e.stopPropagation();

		if (this._disableToProject) {
			return;
		}

		this.DOM.addClass(e.target as HTMLElement, '-clicked');
		let slug = this.DOM.getAttribute(e.target, 'data-slug');
		let title = this.DOM.getAttribute(e.target, 'data-title');
		let href = this.DOM.getAttribute(e.target, 'href');
		let index = parseInt(this.DOM.getAttribute(e.target, 'data-index'), 10) + 1;

		Simple.event('explore_project_press', {
			department: this._department,
			slug: slug
		});

		SiteTransitions.startNormalTransition();
		this._projectSelected(slug, index, href, title);

		return false;
	}

	/** Back to home button click */
	private _onBackToHomeClick(e: MouseEvent = null): boolean {
		if (!this._active || this._isDeepLink) {
			if (this._isDeepLink) {
				Simple.event('back_to_home_button_click', { department: this._department, type: 'deeplink' });
			}

			return;
		}

		if (e) {
			e.preventDefault();
		}

		//Trigger event to switch to home controller view
		let newState: HistoryStateModel = {
			section: SectionType.HOME,
			title: 'Home',
			href: `/`,
			skipNewControllerReset: true
		};

		Events.dispatch(AppEvents.CHANGE_SECTION, newState);


		Simple.event('back_to_home_button_click', { department: this._department, type: 'seamless' });

		return false;
	}

	//Let the main controller know which is selected and that handles letting all controllers know what is going on
	private _projectSelected(slug: string, index: number, href: string, title: string, skipHistoryState = false): void {
		//------------------------------------------

		SiteTransitions.onStartProjectToWorkSwitch = () => {
			this._switchFromProject();
		};

		SiteTransitions.focusedProjectUiTheme = this._paging.focusedUITheme;

		this._disableToProject = true;
		this._drag.allowWindDownUpdates = true;

		this._paging.hide();

		SiteTransitions.startWorkToProject();

		this._scene.activeTile = this._scene.tiles[index];
		this._scene.activeTile.animateToProject();
		this._drag.removeVelocity();
		this._drag.setAllowDrag(false);

		this._scene.hideAllProjects(index);

		globals.activeTile = this._scene.activeTile;
		globals.activeWorkController = this;

		if (this.get('scrollbar').shouldResizeLock()) {
			this._allowResize = false;
		}

		let scaleY =
			(device.innerHeight - ProjectHeaderSizes.headerHeight()) / device.innerHeight;
		scaleY += 0.001;

		//Trigger event to switch to 'work' controller for department:

		let newState: HistoryStateModel = {
			section: SectionType.PROJECT,
			subsection: this._department,
			projectSlug: slug as ProjectSlugType,
			projectIndex: index,
			title: title,
			href: `${href}`,
			skipHistoryState: skipHistoryState
		};
		Events.dispatch(AppEvents.CHANGE_SECTION, newState, { delay: Config.toProjectSectionChangeDelay });

		this.get('UI').hideForProject();

		SiteTransitions.addCounter(() => {
			this._drag.setActive(false);
			this._drag.allowWindDownUpdates = false;
			this.DOM.removeClass(this.container, '-active');
		}, 1000);

		this._disableToProject = true;

		//Nav menu switch:
	}

	private _checkPromptHide() {
		if (!this._trackHidePrompt) {
			return;
		}

		if (Math.abs(this._startDragPromptProgress - this._drag.progress) > 0.02) {
			this._trackHidePrompt = false;
			this._uiController.hideDragPrompt();
		}
	}

	//Scrollwheel / mouse press
	//------------------------------------------------------------------------------------

	private _wheelLocked: boolean = false;
	private timeout;

	private _onMouseWheel(event: DebugMouseWheelEvent): void {
		if (!this._active || this._paging.disableAutoUpdate || globals.activeWorkController) {
			return;
		}

		if (!event.isHuman || this._wheelLocked) {
			return;
		}

		this._gotoProject(constrain(this._paging.getActiveIndex() + -1 * event.delta, 0, this._scene.tiles.length - 1));
		this._wheelLocked = true;

		Simple.event('work_keypress', { department: this._department });

		//Block for at least 200ms to stop Mac accelate at start affect:
		if (this.timeout) clearTimeout(this.timeout);
		this.timeout = setTimeout(() => {
			this._wheelLocked = false;
			this.timeout = null;
		}, 200);
	}

	private _onKeyPress(event: DebugKeypressEvent): void {
		if (!this._active || globals.activeWorkController) {
			return;
		}

		let direction = event.keyType === DirectionalKeyType.LEFT || event.keyType === DirectionalKeyType.PAGE_UP ? -1 : 1;

		Simple.event('work_keypress', {
			direction: direction,
			breakPointIndex: this._paging.getActiveIndex() + direction,
			department: this._department
		});

		this._gotoProject(constrain(this._paging.getActiveIndex() + direction, 0, this._scene.tiles.length - 1));
	}

	/** View ready event */
	private _onViewReady(event: ViewReadyEvent): void {
		if (event.view === ViewType.HOME) {
			this._homeViewReady = true;
		}
	}

	//UTIL (GENERICS):
	//-------------------------------------------------------------------------------------


}
